import type { IUserTableFilters } from 'src/types/user';

import { useBoolean, useSetState } from 'minimal-shared/hooks';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';

import { useTranslate } from 'src/locales';
import { useVoiceCallAllowed } from 'src/hook/use-voice-call-allowed';
import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';
import { usePersistedVisibleColumns } from 'src/hook/usePersistedVisibleColumns';
import { useCampaignContext } from 'src/pages/campaign/hooks/use-campaign-context';
import { useCallContext } from 'src/pages/chat/channel/call/hooks/use-call-context';

import { toast } from 'src/components/snackbar';
import { useTable, rowInPage, getComparator } from 'src/components/table';

import type { TableHeadCellProps } from '../components/head/table-head-custom';

// ----------------------------------------------------------------------

type UseTableCustomLogicProps = {
  columns: TableHeadCellProps[];
  data: any[];
  moduleName: string; // Used for both translation and column management
  rowHeight?: boolean;
  tabFilters?: boolean;
  onQueryChange?: (newParams: any) => void;
  isCampaignTamplate?: boolean;
  isSelectAudience?: boolean;
  isCampaining?: boolean;
  getSelectedRows?: (id: any, selectAllFilter?: any) => void;
  totalRecordLength?: number;
  sortFldList?: string[];
  isEdit?: boolean;
  isDelete?: boolean;
  selectRowPremission?: boolean;
  /** When true, row is omitted from header / bulk "select all" and floating bar select-all. */
  isRowInteractionLocked?: (row: any) => boolean;
};

// ----------------------------------------------------------------------

export function useTableCustomLogic({
  columns,
  data,
  moduleName,
  rowHeight,
  tabFilters,
  onQueryChange = () => {},
  isCampaignTamplate = false,
  isSelectAudience = false,
  isCampaining = false,
  getSelectedRows = () => {},
  totalRecordLength = 0,
  sortFldList,
  selectRowPremission = true,
  isRowInteractionLocked,
}: UseTableCustomLogicProps) {
  const theme = useTheme();
  const { t } = useTranslate('translation');
  const voiceCallAllowed = useVoiceCallAllowed();
  const { makeCall, registered, handleCallUserDetails } = useCallContext();
  const { state: campaignState, setField } = useCampaignContext();

  // State
  const [selectDefault, setSelectDefault] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [tableData, setTableData] = useState<any[]>(data);
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [dateTimeFiltre, setDateTimeFilter] = useState<any>([]);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [resetFilterKey, setResetFilterKey] = useState(0);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<any | null>(null);

  // Dialogs
  const confirmDialog = useBoolean();
  const makeCallDialog = useBoolean(false);

  // Filters
  const userFilters = useSetState<IUserTableFilters>({ name: '', role: [], status: 'ALL' });
  const { state: currentFilters, setState: updateFilters } = userFilters;

  // Table instance
  const table = useTable({
    defaultDense: rowHeight ? false : !tabFilters,
    defaultRowsPerPage: 10,
    defaultselectAll: campaignState.selectAllFilter,
  });

  // Column visibility
  const { visibleColumns, setVisibleColumns } = usePersistedVisibleColumns(columns, moduleName);

  // Permissions
  const { isCreate, isView, isImport, isExport, isEdit, isDelete } =
    useModulePermissions(moduleName);

  const deleteRecordWarning = t('DELETE_RECORD_WARNING').replace(
    '{MODULE}',
    t(moduleName ?? 'UNKNOWN')
  );

  // Sync data
  useEffect(() => {
    setTableData(data);
  }, [data]);

  // Data filtering
  const dataFiltered = useMemo(
    () =>
      applyFilter({
        inputData: tableData,
        comparator: getComparator(table.order, table.orderBy),
        filters: currentFilters,
      }),
    [tableData, table.order, table.orderBy, currentFilters]
  );

  const selectableRowIds = useMemo(
    () =>
      dataFiltered
        .filter((row) => !isRowInteractionLocked?.(row))
        .map((row: any) => row.id),
    [dataFiltered, isRowInteractionLocked]
  );

  const dataInPage = rowInPage(
    dataFiltered.filter((item): item is any => 'subject' in item),
    table.page,
    table.rowsPerPage
  );

  const canReset =
    !!currentFilters.name || currentFilters.role.length > 0 || currentFilters.status !== 'ALL';

  const notFound = (!dataFiltered.length && canReset) || !dataFiltered.length;

  // Handlers
  const handleDeleteRow = useCallback(
    (id: string) => {
      const deleteRow = tableData.filter((row) => row.id !== id);
      toast.success(t('ROW_DELETE_SUCCESS'));
      setTableData(deleteRow);
      table.onUpdatePageDeleteRow(dataInPage.length);
    },
    [dataInPage.length, t, table, tableData]
  );

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    column: string
  ) => {
    table.setPage(0);
    let value = event.target.value;

    if (column === 'phone' && value.startsWith('0')) {
      value = value.slice(1);
    }

    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      if (value === '') {
        delete newFilters[column];
      } else {
        newFilters[column] = value;
      }
      return newFilters;
    });

    if (value === '') {
      setTableData(data);
    }
  };

  const handleDeleteRecord = (id: string) => {
    confirmDialog.onTrue();
    setSelectedRowId(id);
  };

  const handleRemoveAllFilter = () => {
    setFilters({});
    setDateTimeFilter([]);
    updateFilters({ name: '', role: [], status: 'ALL' });
    table.setAdvanceFilter([]);
    table.setPage(0);
    setTableData(data);

    const updatedFilterData = {
      orderBy: table.orderBy ?? '',
      page: 0,
      rowsPerPage: table.rowsPerPage ?? 5,
      globalSearch: '',
      fieldSearch: {},
      order: table.order ?? 'asc',
      TabFilter: 'ALL',
      dateTimeFiltre: [],
      advanceFilter: [],
    };

    onQueryChange(updatedFilterData);
    setResetFilterKey((prev) => prev + 1);
  };

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      table.onResetPage();
      updateFilters({ status: newValue });
    },
    [updateFilters, table]
  );

  const handlePhoneNumberClick = (value: any) => {
    if (!voiceCallAllowed) {
      toast.error(t('VOICE_CALL_NOT_AVAILABLE'));
      return;
    }
    setSelectedPhoneNumber(value);
    makeCallDialog.onTrue();
  };

  const handleSelectAll = () => {
    table.setSelectAllFilter((prev: any) => {
      const newActive = !prev.active;
      return {
        active: newActive,
        filterType: newActive ? 'all' : 'in',
        ids: [],
      };
    });

    if (table.selectAllFilter.active) {
      table.setSelected([]);
    }
  };

  const resetPagination = () => {
    table.setPage(0);
  };

  // Effects
  useEffect(() => {
    if (isFirstLoad) {
      setSelectDefault(false);
      setIsFirstLoad(false);
      return;
    }
    setSelectDefault(false);
  }, [table.selectAllFilter, isFirstLoad]);

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }

    const updatedFilterData = {
      orderBy: table.orderBy ?? '',
      page: table.page,
      rowsPerPage: table.rowsPerPage ?? 5,
      globalSearch: currentFilters.name ?? '',
      fieldSearch: filters ?? [],
      order: table.order ?? 'asc',
      TabFilter: currentFilters.status ?? 'ALL',
      dateTimeFiltre,
      advanceFilter: table.advanceFilter ?? [],
    };
    onQueryChange(updatedFilterData);
  }, [
    currentFilters.name,
    currentFilters.status,
    table.orderBy,
    table.order,
    table.page,
    table.rowsPerPage,
    filters,
    dateTimeFiltre,
    table.advanceFilter,
    isFirstLoad,
    onQueryChange,
  ]);

  useEffect(() => {
    if (isCampaining) return;
    if (!isCampaignTamplate || !isSelectAudience) {
      table.setSelected([]);
    }
  }, [table.page, isCampaining, isCampaignTamplate, isSelectAudience]);

  useEffect(() => {
    if (
      campaignState?.selectedTemplate &&
      campaignState.selectedTemplate.id &&
      isCampaignTamplate
    ) {
      table.setSelected([campaignState.selectedTemplate.id]);
    }
  }, [campaignState?.selectedTemplate?.id, isCampaignTamplate]);

  useEffect(() => {
    if (!getSelectedRows) return;
    getSelectedRows(table.selected, table.selectAllFilter);
  }, [table.selected, getSelectedRows, table.selectAllFilter]);

  useEffect(() => {
    if (!isSelectAudience) return;

    const { active, filterType, ids } = table.selectAllFilter;
    const allIds = dataFiltered.map((row: any) => row.id);
    const firstRenderForedit = selectDefault && campaignState.isEdit;

    if (active) {
      if (filterType === 'all') {
        table.onSelectAllRows(true, firstRenderForedit ? campaignState?.selectedContacts : allIds);
        table.setSelected(firstRenderForedit ? campaignState?.selectedContacts : allIds);
      } else if (filterType === 'not in') {
        const filteredIds = allIds.filter((id: any) => !ids.includes(id));
        table.onSelectAllRows(true, filteredIds);
        table.setSelected(filteredIds);
      }
    } else if (filterType === 'in') {
      const selectedIds = ids.filter((id: any) => allIds.includes(id));
      table.onSelectAllRows(false, selectedIds);
      table.setSelected(selectedIds);
    } else {
      table.setSelected([]);
    }
  }, [table.selectAllFilter, dataFiltered, isSelectAudience, selectDefault, campaignState]);

  useEffect(() => {
    setField('selectAllFilter', table.selectAllFilter);
  }, [table.selectAllFilter, setField]);

  // Column adjustment logic
  const newColumnCount = columns.length;
  const adjustFactor = newColumnCount < 10 ? newColumnCount : newColumnCount - 10 + 1;

  const updateColumns =
    ((!isEdit && !isDelete) || moduleName === 'TENANTS') &&
    moduleName !== 'LEARNING_QUEUE' &&
    moduleName !== 'KNOWLEDGE_GAPS' &&
    moduleName !== 'CSAT'
      ? columns
          .map((item: any) => ({
            ...item,
            width: item?.width + adjustFactor * 100,
          }))
          ?.filter((item) => item?.id !== 'action')
      : columns;

  // Return flattened props instead of grouped
  return {
    // Translation and module info
    t,
    moduleName,

    // Permissions
    isCreate,
    isView,
    isImport,
    isExport,
    isEdit,
    isDelete,

    // Table instance
    table,

    // Toolbar props
    filters: userFilters,
    allColumns: updateColumns, // All columns for the dropdown (including hidden)
    visibleColumns,
    setVisibleColumns,
    onResetPage: table.onResetPage,
    selectedfilters: filters,
    dateTimeFiltre,
    advanceFilters: table.advanceFilter,
    handleRemoveAllFilter,
    resetPagination,

    // Table head props
    order: table.order,
    orderBy: table.orderBy,
    headCells: updateColumns.filter((c) => visibleColumns.includes(c.id)),
    rowCount: dataFiltered.length,
    numSelected: table.selected.length,
    onSort: table.onSort,
    onSelectAllRows: (checked: boolean) => table.onSelectAllRows(checked, selectableRowIds),
    handleFilterChange,
    setDateTimeFilter,
    selectRowPremission,
    sortFldList,
    isCampaignTamplate,
    isCampaining,
    handleToggleSelectAll: handleSelectAll,
    selectAllFilter: table.selectAllFilter,
    count: totalRecordLength,
    defaultBedgecondition: selectDefault && campaignState.isEdit,
    selectedContact: campaignState?.selectedContacts,
    setPage: table.setPage,
    resetFilterKey,

    // Data
    dataFiltered,
    notFound,
    selectableRowIds,
    updateColumns: updateColumns.filter((c) => visibleColumns.includes(c.id)),

    // Dialogs
    confirmDialog,
    makeCallDialog,
    selectedRowId,
    selectedPhoneNumber,
    deleteRecordWarning,
    handleDeleteRecord,
    setSelectedRowId,
    setSelectedPhoneNumber,
    registered,
    makeCall,
    handleCallUserDetails,

    // Filter handlers
    handleFilterStatus,
    handlePhoneNumberClick,

    // Pagination
    page: table.page,
    dense: table.dense,
    onPageChange: table.onChangePage,
    onChangeDense: table.onChangeDense,
    onRowsPerPageChange: table.onChangeRowsPerPage,

    // Other state
    theme,
    currentFilters,
    campaignState,
    selectDefault,
  };
}

// ----------------------------------------------------------------------

type ApplyFilterProps = {
  inputData: any[];
  filters: IUserTableFilters;
  comparator: (a: any, b: any) => number;
};

function applyFilter({ inputData, comparator, filters }: ApplyFilterProps) {
  const { status } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  inputData = stabilizedThis.map((el) => el[0]);

  if (status !== 'ALL') {
    if (inputData.length && 'status' in inputData[0]) {
      inputData = inputData.filter((user: any) => user.status === status);
    }
  }

  return inputData;
}
